import React, { memo, useMemo } from 'react';

import { t } from 'i18next';

import { Box, styled } from '@mui/material';

import { Can, useAbilityContext } from '@/contexts/ability';

import {
  List,
} from '@datanomik/commons-ui';

import { MAIN_MENU_ITEMS, SETTINGS_MENU_ITEMS } from '@/assets/MenuItems';
import { useSidebarConfig } from '../../SidebarConfigContext';

import type { SidebarMenuItemConfig } from '../../types';
import useSidebarMenu from './useSidebarMenu';
import SidebarMenuItem from './SidebarMenuItem';
import SidebarMenuGroup from './SidebarMenuGroup';

type Props = {
  className?: string;
  isSettings?: boolean;
  onAction?: (action: string) => void;
  collapsed?: boolean;
};

const SidebarMenuRaw = ({ className, isSettings, onAction, collapsed }: Props) => {
  const ability = useAbilityContext();
  const { navigationGroupsEnabled } = useSidebarConfig();
  const rawRoutes: SidebarMenuItemConfig[] = isSettings
    ? SETTINGS_MENU_ITEMS
    : MAIN_MENU_ITEMS;

  const sidebarChildKeys = useMemo(() => {
    if (!navigationGroupsEnabled) return new Set<string>();
    const keys = new Set<string>();
    rawRoutes.forEach((route) => {
      route.sidebarChildren?.forEach((child) => keys.add(child.key));
    });
    return keys;
  }, [rawRoutes, navigationGroupsEnabled]);

  const visibleRoutes = useMemo(() => rawRoutes
    .map(({ showInMenu, taskId, sidebarChildren: rawSidebarChildren, ...rest }) => {
      const sidebarChildren = navigationGroupsEnabled ? rawSidebarChildren : undefined;
      const isSidebarGroup = sidebarChildren && sidebarChildren.length > 0;
      const isAbsorbedByGroup = navigationGroupsEnabled && !isSidebarGroup && sidebarChildKeys.has(rest.key);
      const hasVisibleChild = isSidebarGroup && sidebarChildren.some(
        (child) => child.action || (child.taskId != null && ability.can('enabled', child.taskId.toString())),
      );

      const isGroupWithoutTask = isSidebarGroup && !taskId;

      const isDivider = rest.type === 'divider';

      return {
        ...rest,
        taskId,
        sidebarChildren,
        showInMenu:
          showInMenu &&
          !isAbsorbedByGroup &&
          ((isDivider && navigationGroupsEnabled) || (
            (!taskId || ability.can('allowed', taskId.toString())) &&
            (isGroupWithoutTask ? hasVisibleChild : ability.can('enabled', rest.key))
          )),
      };
    })
    .filter(({ showInMenu }) => showInMenu)
    .filter((item, index, arr) => {
      if (item.type !== 'divider') return true;
      const prev = arr[index - 1];
      const next = arr[index + 1];
      // Remove divider if there's nothing before it, nothing after it, or it's adjacent to another divider
      return prev != null && next != null && prev.type !== 'divider' && next.type !== 'divider';
    }), [rawRoutes, ability, sidebarChildKeys, navigationGroupsEnabled]);

  const { isActive, isSubItemActive, createUrl } =
    useSidebarMenu(visibleRoutes);

  return (
    <Box className={className} sx={{ position: 'relative' }}>
      <List sx={{ padding: 0 }}>
        {visibleRoutes.map((item) => {
          const { key, type, path, label, icon, link, isNew, isBeta, sidebarChildren, children } = item;

          if (type === 'divider') {
            return (
              <Box component="li" key={key} className="sidebar-divider" sx={{ position: 'relative', height: '7px', listStyle: 'none', overflow: 'hidden', transition: 'height 0.25s ease-in-out' }}>
                <Box sx={{ position: 'absolute', left: 0, right: 0, top: '50%', height: '1px', bgcolor: 'rgba(0,0,0,0.06)' }} />
              </Box>
            );
          }

          const isEnabled = ability.can('enabled', key);

          const visibleSidebarChildren = (sidebarChildren ?? []).filter(
            (child) => child.action || (child.taskId != null && ability.can('enabled', child.taskId.toString())),
          );
          const hasGroup = visibleSidebarChildren.length > 0;

          if (hasGroup) {
            return (
              <Can I="view" a={key} key={key}>
                <SidebarMenuGroup
                  item={item}
                  visibleChildren={visibleSidebarChildren}
                  createUrl={createUrl}
                  isSubItemActive={isSubItemActive}
                  isActive={isActive}
                  onAction={onAction}
                />
              </Can>
            );
          }

          const subitems = (children?.filter((c) => c.showInMenu) ?? []).filter(
            (c) => c.path,
          );
          const hasSubitems = subitems.length > 0;

          let to = isEnabled ? link || createUrl(`/${path}`) : undefined;
          if (hasSubitems && to) {
            to = createUrl(`/${path}/${subitems[0].path}`);
          }

          return (
            <Can I="view" a={key} key={key}>
              <SidebarMenuItem
                label={t(label ?? '')}
                to={to}
                icon={icon}
                isSelected={isActive(path)}
                isEnabled={isEnabled}
                isNew={isNew}
                isBeta={isBeta}
                link={link}
              />
            </Can>
          );
        })}
      </List>
    </Box>
  );
};

const SidebarMenu = styled(SidebarMenuRaw)(() => ({
  '.dn-menu-icon-lock': {
    fontSize: '16px !important',
    background: '#fff',
    borderRadius: '500px',
    border: '1px solid rgba(0, 0, 0, 0)',
    filter: 'drop-shadow(1px 0px 2px rgba(0, 0, 0, 0.25)) !important',
    padding: '2px',
  },
}));

export default memo(SidebarMenu);
